import React, { useState, useCallback, useEffect } from 'react';
import { RadioButton } from '@framework-controls/core';
import { strings } from 'src/localization/index';
import css from './MobileDiagnose.module.scss';
import { diagnoseResponses } from '../DiagnoseTreeLit/DiagnoseTreeLit';
import { IndexMapInterface } from '../WriteYourOwn';
import MobileDiagnoseContent from './MobileDiagnoseContent/MobileDiagnoseContent';
import MobilePreviousList from './MobilePreviousList/MobilePreviousList';

interface IProps {
    // Adds the selected diagnosis to the list
    addDiagnosis: (title: string, additionalComments: string, diagnosticOpType?: boolean) => void;
    // A map of the currently selected dialog tree path
    indexMap: IndexMapInterface[];
    // Toggles the display of the clicked previous item group
    toggleDisplay: (index: number) => void;
    // Handles selection of the next option
    selectButton: (value: string) => void;
    // Handles changing a previously selected option
    selectPrevButton: (index: string, selected: string) => void;
    title?: string;
    // Tracks if user has reached the final dialog in the tree or has chosen Other and entered comments
    unenteredDiagnosis: boolean;
    // True if user selects Yes from Unentered Issue Warning
    addUnenteredDiagnosis: boolean;
    // Handles change for reaching final dialog in tree or Other + comments
    resetMobileDiagnose: boolean;
    setResetMobileDiagnose: (boolean: boolean) => void;
    // Triggers warning if user attempts to proceed without using Add Diagnosis button
    setUnenteredDiagnosis: (boolean: boolean) => void;
    // The indication toggled when user confirms an unentered Diagnose Tool issue should be added
    setAddUnenteredDiagnosis: (boolean: boolean) => void;
    // Manages unentered title from diagnose tool
    setUnenteredTitle: (title: string) => void;
    // Manages unentered comments from diagnose tool
    setUnenteredComments: (comments: string) => void;
    theme: string;
}

const Diagnose = (props: IProps) => {
    const [selectedButton, setSelectedButton] = useState('');
    const [additionalComments, setAdditionalComments] = useState('');


    const diagnoseLiterals = [
        {
            option: '',
            title: strings.WhichApplies,
            responses: [
                {
                    option: strings.IFeelIt,
                    underlined: strings.Feel,
                    title: strings.IFeel,
                    responses: [
                        {
                            option: strings.VehicleDrift,
                            title: strings.VehicleDriftTo,
                            responses: [
                                {
                                    option: strings.Left,
                                    title: strings.VehicleDriftToLeft,
                                    responses: [],
                                },
                                {
                                    option: strings.Right,
                                    title: strings.VehicleDriftToRight,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.PlaySteeringWheel,
                            title: strings.FeelPlaySteeringWheel,
                            responses: [],
                        },
                        {
                            option: strings.PulsingVibrations,
                            title: strings.FeelPulsingVibrations,
                            responses: [
                                {
                                    option: strings.FrontVehicle,
                                    title: strings.PulsingVibrationsFront,
                                    responses: [
                                        {
                                            option: strings.WhenAccelerating,
                                            title: strings.PulsingVibrationsFrontAccelerating,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenBraking,
                                            title: strings.PulsingVibrationsFrontBraking,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HigherSpeeds,
                                            title: strings.PulsingVibrationsFrontHigh,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.PulsingVibrationsFrontAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PulsingVibrationsFrontNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.RearVehicle,
                                    title: strings.PulsingVibrationsRear,
                                    responses: [
                                        {
                                            option: strings.WhenAccelerating,
                                            title: strings.PulsingVibrationsRearAccelerating,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenBraking,
                                            title: strings.PulsingVibrationsRearBraking,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HigherSpeeds,
                                            title: strings.PulsingVibrationsRearHigh,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.PulsingVibrationsRearAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PulsingVibrationsRearNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.SteeringWheel,
                                    title: strings.PulsingVibrationsSteeringWheel,
                                    responses: [
                                        {
                                            option: strings.WhenAccelerating,
                                            title: strings.PulsingVibrationsSteeringWheelAccelerating,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenBraking,
                                            title: strings.PulsingVibrationsSteeringWheelBraking,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenTurning,
                                            title: strings.PulsingVibratingSteeringWheelTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HigherSpeeds,
                                            title: strings.PulsingVibratingSteeringWheelSpeeds,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.PulsingVibratingSteeringWheelAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PulsingVibrationsSteeringWheelNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.PulsingVibrationsVehicle,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Swaying,
                            title: strings.SwayingSideToSide,
                            responses: [],
                        },
                        {
                            option: strings.None,
                            title: strings.FeelsDifferent,
                            responses: [],
                        },
                    ],
                },
                {
                    option: strings.IHearIt,
                    underlined: strings.Hear,
                    title: strings.IHear,
                    responses: [
                        {
                            option: strings.SquealingChirping,
                            title: strings.SquealingChirpingFrom,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SquealingChirpingEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SquealingChirpingEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenStopping,
                                            title: strings.SquealingChirpingEngineStopping,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.SquealingChirpingEngineStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.SquealingChirpingEngineTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.SquealingChirpingEngineMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SquealingChirpingEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.SquealingChirpingInterior,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SquealingChirpingInteriorAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HeatOrAC,
                                            title: strings.SquealingChirpingInteriorHeatAC,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SquealingChirpingInteriorNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.SquealingChirpingUnder,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SquealingChirpingUnderAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenStopping,
                                            title: strings.SquealingChirpingUnderStopping,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.SquealingChirpingUnderTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.SquealingChirpingUnderMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SquealingChirpingUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            option: strings.Hissing,
                            title: strings.HearHissing,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.HissingEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.HissingEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.HissingEngineStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.HissingEngineMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.HissingEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.HissingInterior,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.HissingInteriorAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HeatOrAC,
                                            title: strings.HissingInteriorHeatAC,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.HissingInteriorNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.FromTire,
                                    title: strings.HissingTire,
                                    responses: [
                                        {
                                            option: strings.DriversSideFront,
                                            title: strings.HissingTireDriversSideFront,
                                            responses: [],
                                        },
                                        {
                                            option: strings.DriversSideRear,
                                            title: strings.HissingTireDriversSideRear,
                                            responses: [],
                                        },
                                        {
                                            option: strings.PassengersSideFront,
                                            title: strings.HissingTirePassengersSideFront,
                                            responses: [],
                                        },
                                        {
                                            option: strings.PassengersSideRear,
                                            title: strings.HissingTirePassengersSideRear,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OtherTire,
                                            title: strings.HissingOtherTire,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.HissingNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Knocking,
                            title: strings.HearKnocking,
                            responses: [
                                {
                                    option: strings.AllTheTime,
                                    title: strings.HearKnockingAll,
                                    responses: [],
                                },
                                {
                                    option: strings.OnlyWhenStarting,
                                    title: strings.KnockingStarting,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.KnockingNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Tapping,
                            title: strings.HearTapping,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.TappingEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.TappingEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.TappingEngineStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.TappingEngineMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.TappingEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.TappingUnder,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.TappingUnderAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.TappingUnderStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.TappingUnderMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.TappingUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.TappingNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Rattle,
                            title: strings.HearRattle,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.RattleEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.RattleEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.RattleEngineStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhileMoving,
                                            title: strings.RattleEngineMoving,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.RattleEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.RattleUnder,
                                    responses: [],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.RattleInterior,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.RattleInteriorAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.HeatOrAC,
                                            title: strings.RattleInteriorHeatAC,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.RattleInteriorNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.RattleNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Grinding,
                            title: strings.HearGrinding,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.GrindingEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.GrindingEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenStopping,
                                            title: strings.GrindingEngineStopping,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenStarting,
                                            title: strings.GrindingEngineStarting,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.GrindingEngineTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.GrindingEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.GrindingUnder,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.GrindingUnderAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.WhenStopping,
                                            title: strings.GrindingUnderStopping,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.GrindingUnderTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.GrindingUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.GrindingNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Clunk,
                            title: strings.HearClunk,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.ClunkEngine,
                                    responses: [
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.ClunkEngineTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Bumps,
                                            title: strings.ClunkEngineBumps,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Gear,
                                            title: strings.ClunkEngineGear,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.ClunkEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.ClunkUnder,
                                    responses: [
                                        {
                                            option: strings.OnlyWhenTurning,
                                            title: strings.ClunkUnderTurning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Bumps,
                                            title: strings.ClunkUnderBumps,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Gear,
                                            title: strings.ClunkUnderGear,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.ClunkUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.ClunkNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.None,
                            title: strings.SoundNone,
                            responses: [],
                        },
                    ],
                },
                {
                    option: strings.ISeeIt,
                    underlined: strings.See,
                    title: strings.ISee,
                    responses: [
                        {
                            option: strings.Smoke,
                            title: strings.SeeSmoke,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SeeSmokeEngine,
                                    responses: [
                                        {
                                            option: strings.AppearsBlack,
                                            title: strings.BlackSmokeEngine,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AppearsBlue,
                                            title: strings.BlueSmokeEngine,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AppearsWhite,
                                            title: strings.WhiteSmokeEngine,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmokeEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.ExhaustPipe,
                                    title: strings.SmokeExhaust,
                                    responses: [
                                        {
                                            option: strings.AppearsBlack,
                                            title: strings.SmokeExhaustBlack,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AppearsBlue,
                                            title: strings.SmokeExhaustBlue,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AppearsWhite,
                                            title: strings.SmokeExhaustWhite,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmokeExhaustNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.SmokeInterior,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.PuddleLeak,
                            title: strings.SeePuddleLeak,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.PuddleLeakEngine,
                                    responses: [
                                        {
                                            option: strings.EngineOil,
                                            title: strings.PuddleLeakEngineOil,
                                            responses: [],
                                        },
                                        {
                                            option: strings.TransmissionFluid,
                                            title: strings.PuddleLeakEngineTransmissionFluid,
                                            responses: [],
                                        },
                                        {
                                            option: strings.PowerSteeringFluid,
                                            title: strings.PuddleLeakEnginePowerSteeringFluid,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Coolant,
                                            title: strings.PuddleLeakEngineCoolant,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Gasoline,
                                            title: strings.PuddleLeakEngineGasoline,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PuddleLeakEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.PuddleLeakUnder,
                                    responses: [
                                        {
                                            option: strings.EngineOil,
                                            title: strings.PuddleLeakUnderEngineOil,
                                            responses: [],
                                        },
                                        {
                                            option: strings.TransmissionFluid,
                                            title: strings.PuddleLeakUnderTransmissionFluid,
                                            responses: [],
                                        },
                                        {
                                            option: strings.PowerSteeringFluid,
                                            title: strings.PuddleLeakUnderPowerSteeringFluid,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Coolant,
                                            title: strings.PuddleLeakUnderCoolant,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Gasoline,
                                            title: strings.PuddleLeakUnderGasoline,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PuddleLeakUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.PuddleLeakInterior,
                                    responses: [
                                        {
                                            option: strings.Water,
                                            title: strings.PuddleLeakInteriorWater,
                                            responses: [],
                                        },
                                        {
                                            option: strings.Refrigerant,
                                            title: strings.PuddleLeakInteriorRefrigerant,
                                            responses: [],
                                        },
                                        {
                                            option: strings.CoolantSmell,
                                            title: strings.PuddleLeakInteriorCoolant,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.PuddleLeakInteriorNone,
                                            responses: [],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            option: strings.LowFlatTire,
                            title: strings.SeeLowFlatTire,
                            responses: [
                                {
                                    option: strings.OnDriversSideFront,
                                    title: strings.LowFlatTireDriversSideFront,
                                    responses: [],
                                },
                                {
                                    option: strings.OnDriversSideRear,
                                    title: strings.LowFlatTireDriversSideRear,
                                    responses: [],
                                },
                                {
                                    option: strings.OnPassengersSideFront,
                                    title: strings.LowFlatTirePassengersSideFront,
                                    responses: [],
                                },
                                {
                                    option: strings.OnPassengersSideRear,
                                    title: strings.LowFlatTirePassengersSideRear,
                                    responses: [],
                                },
                                {
                                    option: strings.OtherTire,
                                    title: strings.LowFlatTireNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.WarningLight,
                            title: strings.ISeeThe,
                            responses: [
                                {
                                    option: strings.CheckEngineLight,
                                    title: strings.SeeCheckEngineLight,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.CheckEngineLightOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.CheckEngineLightConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.TirePressureWarning,
                                    title: strings.SeeTirePressureWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.TirePressureWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.TirePressureWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.CoolantTemperatureWarning,
                                    title: strings.SeeCoolantTemperatureWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.CoolantTemperatureWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.CoolantTemperatureWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.BatteryChargeWarning,
                                    title: strings.SeeBatteryChargeWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.BatteryChargeWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.BatteryChargeWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.OilPressureWarning,
                                    title: strings.SeeOilPressureWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.OilPressureWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.OilPressureWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.BrakeABSWarning,
                                    title: strings.SeeBrakeABSWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.BrakeABSWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.BrakeABSWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.StabilityTractionWarning,
                                    title: strings.SeeStabilityTractionWarning,
                                    responses: [
                                        {
                                            option: strings.OccasionallyOn,
                                            title: strings.StabilityTractionWarningOccasionally,
                                            responses: [],
                                        },
                                        {
                                            option: strings.ConstantlyOn,
                                            title: strings.StabilityTractionWarningConstantly,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.OtherWarningLight,
                                    title: strings.WarningLightOther,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.None,
                            title: strings.SomethingLooksDifferent,
                            responses: [],
                        },
                    ],
                },
                {
                    option: strings.ISmellIt,
                    underlined: strings.Smell,
                    title: strings.ISmell,
                    responses: [
                        {
                            option: strings.Gasoline,
                            title: strings.SmellGasoline,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SmellGasolineEngine,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SmellGasolineEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.EngineRunning,
                                            title: strings.SmellGasolineEngineRunning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmellGasolineEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Under,
                                    title: strings.SmellGasolineUnder,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SmellGasolineUnderAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.EngineRunning,
                                            title: strings.SmellGasolineUnderRunning,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmellGasolineUnderNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.None,
                                    title: strings.SmellGasolineNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.RottenEggsSulfur,
                            title: strings.SmellRottenEggsSulfur,
                            responses: [],
                        },
                        {
                            option: strings.Mildew,
                            title: strings.SmellMildew,
                            responses: [
                                {
                                    option: strings.AllTheTime,
                                    title: strings.SmellMildewAll,
                                    responses: [],
                                },
                                {
                                    option: strings.HeatOrAC,
                                    title: strings.SmellMildewHeatAC,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.SmellMildewNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.Antifreeze,
                            title: strings.SmellAntrifreeze,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SmellAntiFreezeEngine,
                                    responses: [],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.SmellAntiFreezeInterior,
                                    responses: [
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SmellAntiFreezeInteriorAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.OnlyWhenHeat,
                                            title: strings.SmellAntiFreezeInteriorHeat,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmellAntifreezeNone,
                                            responses: [],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            option: strings.BurningOil,
                            title: strings.SmellBurningOil,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SmellBurningOilEngine,
                                    responses: [],
                                },
                                {
                                    option: strings.ExhaustPipe,
                                    title: strings.SmellBurningOilExhaust,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.SmellBurningOilNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.ExhaustGas,
                            title: strings.SmellExhaustGas,
                            responses: [
                                {
                                    option: strings.TheEngine,
                                    title: strings.SmellExhaustGasEngine,
                                    responses: [
                                        {
                                            option: strings.OnlyDuringStartup,
                                            title: strings.SmellExhaustGasEngineStartup,
                                            responses: [],
                                        },
                                        {
                                            option: strings.AllTheTime,
                                            title: strings.SmellExhaustGasEngineAll,
                                            responses: [],
                                        },
                                        {
                                            option: strings.None,
                                            title: strings.SmellExhaustGasEngineNone,
                                            responses: [],
                                        },
                                    ],
                                },
                                {
                                    option: strings.Interior,
                                    title: strings.SmellExhaustGasInterior,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.SmellExhaustGasNone,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.None,
                            title: strings.SmellNone,
                            responses: [],
                        },
                    ],
                },
                {
                    option: strings.VehicleNotFunction,
                    underlined: strings.NotFunction,
                    title: strings.MyVehicles,
                    responses: [
                        {
                            option: strings.EngineWontStart,
                            title: strings.MyVehiclesEngine,
                            responses: [
                                {
                                    option: strings.StartsStalls,
                                    title: strings.EngineStartsStalls,
                                    responses: [],
                                },
                                {
                                    option: strings.CranksWontRun,
                                    title: strings.EngineCranksWontRun,
                                    responses: [],
                                },
                                {
                                    option: strings.DoesntCrank,
                                    title: strings.EngineDoesntCrank,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.VehiclesEngineWontStart,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.VentsWontBlowHot,
                            title: strings.MyVehiclesVents,
                            responses: [
                                {
                                    option: strings.OnlyColdAir,
                                    title: strings.VehicleOnlyColdAir,
                                    responses: [],
                                },
                                {
                                    option: strings.WontBlowAir,
                                    title: strings.NoAir,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.WontBlowHot,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.VentsWontBlowCold,
                            title: strings.MyVehiclesVents,
                            responses: [
                                {
                                    option: strings.OnlyWarmAir,
                                    title: strings.VehicleOnlyWarmAir,
                                    responses: [],
                                },
                                {
                                    option: strings.WontBlowAir,
                                    title: strings.NoAir,
                                    responses: [],
                                },
                                {
                                    option: strings.None,
                                    title: strings.WontBlowCold,
                                    responses: [],
                                },
                            ],
                        },
                        {
                            option: strings.EngineRunsVehicleWontMove,
                            title: strings.VehiclesEngineRunsVehicleWontMove,
                            responses: [],
                        },
                        {
                            option: strings.None,
                            title: strings.MyVehicle,
                            responses: [],
                        },
                    ],
                },
                {
                    option: strings.OtherNone,
                    title: strings.DescribeVehiclesIssue,
                    responses: [],
                },
            ],
        },
    ];

    const addHandler = useCallback((title: string) => {
        setSelectedButton('');
        setAdditionalComments('');
        props.addDiagnosis(title, additionalComments);
    }, [props.addDiagnosis, additionalComments]);

    const selectButtonHandler = useCallback((value: string) => {
        setSelectedButton('');
        props.selectButton(value);
    }, [props.selectButton]);

    let responses: diagnoseResponses[] = diagnoseLiterals[0].responses;
    let title = diagnoseLiterals[0].title;
    let option = '';

    // Use the indexMap to build each previous radioItems and then pass down to the MobilePreviousList
    const previousList = props.indexMap.map((indexMapItem, index) => {
        const radioItems = responses.map((response, index) => (
            <div className={css.radioButton} key={index}>
                <RadioButton
                    value={index.toString()}
                    color={index.toString() === selectedButton ? 'secondary' : 'default'}
                />
                <span>{response.option}</span>
            </div>
        ));

        option = responses[indexMapItem.index].option;
        title = responses[indexMapItem.index].title;
        responses = responses[indexMapItem.index].responses;

        // if (radioItems.length > 0) {
        return (
            <MobilePreviousList
                key={index}
                radioItems={radioItems}
                toggleDisplay={props.toggleDisplay}
                index={index}
                option={option}
                selectPrevButtonHandler={props.selectPrevButton}
                indexMapItem={indexMapItem}
            />
        );
        // }

        // return null;
    });

    let header: JSX.Element | null = <h5 id='h3' className={css.header}>{props.title ? props.title : strings.PleaseDescribe}</h5>;
    if (props.indexMap.length > 0) {
        header = null;
        if (responses.length > 0) {
            header = <h5 id='h3' className={css.header}>{title}</h5>;
        }
    }

    let endDialogTree = false;
    let otherResponse = false;
    // If no responses exist, then we are at the end of the dialog tree.
    if (responses.length === 0) {
        endDialogTree = true;
        // If "Other" is selected
        if (title === strings.DescribeVehiclesIssue) {
            otherResponse = true;
        }
    }

    useEffect(() => {
        // Reset the Diagnosis tree
        if (props.resetMobileDiagnose) {
            setAdditionalComments('');
            setSelectedButton('');
            props.setResetMobileDiagnose(false);
        }
    }, [props.resetMobileDiagnose, props.setResetMobileDiagnose]);

    useEffect(() => {
        // Add an unenteredDiagnosis (if not adding both Diagnosis and Manually written issue)
        if (props.addUnenteredDiagnosis) {
            if (otherResponse && additionalComments) {
                props.addDiagnosis(additionalComments, '', true);
                setAdditionalComments('');
            }
            if (endDialogTree && !otherResponse) {
                props.addDiagnosis(title, additionalComments, true);
            }
            props.setAddUnenteredDiagnosis(false);
        }
    }, [additionalComments, endDialogTree, otherResponse,
        props.addDiagnosis, props.addUnenteredDiagnosis, props.setAddUnenteredDiagnosis, title]);

    useEffect(() => {
        // Track unentered diagnosis and its parts.
        if ((endDialogTree && !otherResponse) || (otherResponse && additionalComments)) {
            props.setUnenteredDiagnosis(true);
            props.setUnenteredTitle(title);
            props.setUnenteredComments(additionalComments);
        } else {
            props.setUnenteredDiagnosis(false);
        }
    }, [additionalComments, endDialogTree, otherResponse, props.setAddUnenteredDiagnosis,
        props.setUnenteredComments, props.setUnenteredDiagnosis, props.setUnenteredTitle, title]);

    return (
        <div className={css.diagnose}>
            <h4 id='h2' className={(props.theme === 'mazda' && css.headerMazda) ||
                (props.theme === 'genesis' && css.headerGenesis)
            }>{strings.Diagnose}</h4>
            {previousList}
            {header}
            <MobileDiagnoseContent
                endDialogTree={endDialogTree}
                otherResponse={otherResponse}
                title={title}
                responses={responses}
                additionalComments={additionalComments}
                onChangeHandler={(value: string) => setAdditionalComments(value)}
                addHandler={addHandler}
                selectedButton={selectedButton}
                selectButtonHandler={selectButtonHandler}
                theme={props.theme}
            />
        </div>
    );
};

export default Diagnose;